// Degraded / offline adapters.
//
// Used when the backend isn't configured (no Supabase env) or native purchases
// aren't available (plain web build, where the RevenueCat Capacitor plugin has
// no implementation). The app stays usable in a free, anonymous state instead of
// crashing — generation simply routes through the paywall path.

import type {
  AuthPort,
  BackendPort,
  FounderStatus,
  GenerateResponse,
  PurchasePort,
  SessionUser,
  TokenProvider,
} from '../bridge/ports';
import { BackendError } from '../bridge/ports';

const ANON_USER: SessionUser = { id: '', email: null, isAnonymous: true };

/** No-backend auth: a single anonymous, token-less session. */
export class OfflineAuth implements AuthPort, TokenProvider {
  async getUser(): Promise<SessionUser | null> {
    return null;
  }
  async ensureSession(): Promise<SessionUser> {
    return ANON_USER;
  }
  async signInWithProvider(): Promise<SessionUser> {
    return ANON_USER;
  }
  async signInWithEmail(): Promise<void> {
    /* no-op */
  }
  async signOut(): Promise<void> {
    /* no-op */
  }
  onAuthChange(_cb: (user: SessionUser | null) => void): () => void {
    return () => {};
  }
  async getAccessToken(): Promise<string | null> {
    return null;
  }
}

/** No-store purchases: always free, buying is unavailable. */
export class OfflinePurchase implements PurchasePort {
  async getActiveEntitlements(): Promise<string[]> {
    return [];
  }
  async identify(): Promise<void> {
    /* no-op */
  }
  async purchase(): Promise<string[]> {
    throw new Error('purchases_unavailable: native store not available in this build');
  }
  async restore(): Promise<string[]> {
    return [];
  }
  onEntitlementsChange(): () => void {
    return () => {};
  }
}

/** No-backend: zero balance, generation always routes to the paywall. */
export class OfflineBackend implements BackendPort {
  async generate(): Promise<GenerateResponse> {
    // No credits offline -> behave exactly like a server 402 so the controller
    // opens the paywall instead of surfacing an opaque error.
    throw new BackendError('insufficient_credits', 402);
  }
  async getBalance(): Promise<number> {
    return 0;
  }
  async founderStatus(): Promise<FounderStatus> {
    return { claimed: 0, remaining: 0, total: 10000, active: false };
  }
  async topUp(): Promise<number> {
    // No backend → no server-authoritative ledger to credit. Surface a typed
    // error so the paywall button shows a retry state instead of silently
    // pretending credits were added.
    throw new BackendError('topup_unavailable', 503);
  }
}
